<section class="mobile-projects-view container-fluid px-3" aria-label="Mobile projects overview">
  <div class="mobile-error-state" *ngIf="hasError" role="alert">
    <mat-icon aria-hidden="true">error_outline</mat-icon>
    <span>{{ errorMessage || 'Failed to load projects. Please try again.' }}</span>
  </div>

  <ng-container *ngIf="!hasError">
    <div class="row g-2 mobile-summary-row">
      <div class="col-6">
        <article class="mobile-summary-card card h-100 flex-row align-items-center gap-2 p-2 rounded-4 shadow-sm">
          <div class="mobile-summary-icon projects-summary-icon">{{ projects.length }}</div>
          <div>
            <div class="mobile-summary-label">Projects</div>

            <div class="mobile-summary-meta">{{ totalExperiments }} experiments</div>
          </div>
        </article>
      </div>
      <div class="col-6">
        <article class="mobile-summary-card card h-100 flex-row align-items-center gap-2 p-2 rounded-4 shadow-sm">
          <div class="mobile-summary-icon batches-summary-icon">{{ openBatches }}</div>
          <div>
            <div class="mobile-summary-label">Open Batches</div>
            <div class="mobile-summary-meta">{{ batchesInProgress }} in progress</div>
          </div>
        </article>
      </div>
    </div>

    <div class="mobile-projects-heading d-flex align-items-center justify-content-between">
      <h3 class="h5 mb-0">Projects</h3>
    </div>

    <div class="mobile-search mb-3">
      <app-project-search
        placeholder="Search for Projects, Experiments & Batches"
        [searchTerm]="searchTerm"
        [showClearButton]="true"
        (search)="search.emit($event)"
      ></app-project-search>
    </div>

    <div class="mobile-project-list" *ngIf="filteredProjects.length > 0; else noProjects">
      <mat-expansion-panel
        class="mobile-project-panel"
        *ngFor="let project of filteredProjects; trackBy: trackByProjectId"
        [expanded]="isProjectExpanded(project)"
        (opened)="openProject(project)"
        (closed)="closeProject(project)"
      >
        <mat-expansion-panel-header>
          <mat-panel-title class="mobile-project-title">
            <span class="mobile-project-icon">
              <app-icon-display *ngIf="project.icon" [icon]="project.icon" [cssClass]="'size-20'"></app-icon-display>
            </span>
            <span class="mobile-title-content">
              <span class="mobile-title-text" [matTooltip]="project.project_title || ''">{{
                project.project_title || 'Untitled project'
              }}</span>
              <small>{{ getProjectExperiments(project).length }} Experiments</small>
            </span>
          </mat-panel-title>
        </mat-expansion-panel-header>

        <div class="mobile-experiments" *ngIf="getProjectExperiments(project).length > 0; else noExperiments">
          <div class="mobile-section-label">Experiments</div>
          <mat-expansion-panel
            class="mobile-experiment-panel"
            *ngFor="let experiment of getProjectExperiments(project)"
            [expanded]="isExperimentExpanded(project, experiment)"
            (opened)="openExperiment(project, experiment)"
            (closed)="closeExperiment(project, experiment)"
          >
            <mat-expansion-panel-header>
              <mat-panel-title class="mobile-experiment-title">
                <mat-icon class="mobile-experiment-icon" [svgIcon]="'experiments'"></mat-icon>
                <span class="mobile-title-content">
                  <span class="mobile-title-text" [matTooltip]="experiment.experimentTitle || ''">{{
                    experiment.experimentTitle || 'Untitled experiment'
                  }}</span>
                  <small class="mobile-status">
                    <span class="mobile-status-dot"></span>{{ statusTitle(experiment.experimentStatus) | uppercase }}
                  </small>
                </span>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="mobile-batches" *ngIf="experiment.experimentBatches?.length; else noBatches">
              <div class="mobile-section-label">Batches</div>
              <ng-container
                *ngFor="let batch of getExperimentBatches(experiment); let batchIndex = index; trackBy: trackByBatchId"
              >
                <div
                  class="mobile-batch-row mobile-parent-batch-row list-group-item d-flex align-items-center"
                  #batchRow
                  role="button"
                  tabindex="0"
                  [attr.data-batch-id]="batch.id"
                  [attr.aria-label]="batchDisplayName(batch, batchIndex)"
                  [class.mobile-batch-row-selected]="isBatchSelected(batch)"
                  (click)="openBatchNotes(batch)"
                  (keydown.enter)="openBatchNotes(batch)"
                  (keydown.space)="$event.preventDefault(); openBatchNotes(batch)"
                >
                  <span class="mobile-batch-version">{{ batchVersionLabel(batch, batchIndex) }}</span>
                  <span class="mobile-batch-title-wrapper" *ngIf="batchTitleLabel(batch)">
                    <span>(</span>
                    <span class="mobile-batch-title">{{ batchTitleLabel(batch) }}</span>
                    <span>)</span>
                  </span>
                  <span class="mobile-batch-actions">
                    <button mat-icon-button type="button" aria-label="Batch notes">
                      <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon>
                    </button>
                  </span>
                </div>
                <div
                  class="mobile-batch-row mobile-split-batch-row list-group-item d-flex align-items-center"
                  #batchRow
                  role="button"
                  tabindex="0"
                  *ngFor="let splitBatch of getChildBatches(batch); let splitIndex = index; trackBy: trackByBatchId"
                  [attr.data-batch-id]="splitBatch.id"
                  [attr.aria-label]="batchDisplayName(splitBatch, splitIndex)"
                  [class.mobile-batch-row-selected]="isBatchSelected(splitBatch)"
                  (click)="openBatchNotes(splitBatch, batch)"
                  (keydown.enter)="openBatchNotes(splitBatch, batch)"
                  (keydown.space)="$event.preventDefault(); openBatchNotes(splitBatch, batch)"
                >
                  <span class="mobile-batch-version">{{ batchVersionLabel(splitBatch, splitIndex) }}</span>
                  <span class="mobile-batch-title-wrapper" *ngIf="batchTitleLabel(splitBatch)">
                    <span>(</span>
                    <span class="mobile-batch-title">{{ batchTitleLabel(splitBatch) }}</span>
                    <span>)</span>
                  </span>
                  <span class="mobile-batch-actions">
                    <button mat-icon-button type="button" aria-label="Split batch notes">
                      <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon>
                    </button>
                  </span>
                </div>
              </ng-container>
            </div>
            <ng-template #noBatches>
              <div class="mobile-empty-inline">No batches available</div>
            </ng-template>
          </mat-expansion-panel>
        </div>
        <ng-template #noExperiments>
          <div class="mobile-empty-inline">No experiments available</div>
        </ng-template>
      </mat-expansion-panel>
    </div>
    <ng-template #noProjects>
      <div class="mobile-empty-state">No project data available</div>
    </ng-template>
  </ng-container>
</section>
